import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { Search, Loader2, X, AlertCircle, FolderOpen } from 'lucide-react';
import { processingApi } from '../../../services/processingApi';
import { chronologyApi, type ChronologyEvent } from '../../../services/chronologyApi';
import { useDocumentProgress } from '../../../contexts/DocumentProgressContext';
import { useAskSidebar } from '../../../contexts/AskSidebarContext';
import { DocumentsViewSwitcher, type DocumentsView } from './DocumentsViewSwitcher';
import { DocumentSummaryCardList } from './DocumentSummaryCardList';
import { EnhancedSearchResults } from './EnhancedSearchResults';
import { BookmarksView } from './BookmarksView';
import { ReadingProgressIndicator } from './ReadingProgressIndicator';

interface ChunkSearchResult {
  documentId: string;
  pageNumber: number | null;
  endPageNumber: number | null;
  content: string;
  chunkIndex: number;
  score: number;
  hasKeywordMatch: boolean;
}

interface DocumentTypeClassification {
  documentId: string;
  type: string;
  confidence: number;
  summary?: string;
  citations?: any[];
}

interface MetadataExtractionResponse {
  documentTypes: DocumentTypeClassification[];
  [key: string]: any;
}

interface DocumentsExplorerProps {
  caseId: string;
  documents: Array<{ documentId: string; filename: string; pageCount?: number; processingStatus?: string }>;
  metadata: MetadataExtractionResponse | null | undefined;
  onOpenDocument: (request: { documentId: string; documentName: string; pageNumber?: number; searchText?: string }) => void;
}

export const DocumentsExplorer: React.FC<DocumentsExplorerProps> = ({
  caseId,
  documents,
  metadata,
  onOpenDocument,
}) => {
  const { viewSummary, refreshViewSummary, bookmarks, refreshBookmarks, removeBookmark } = useDocumentProgress();
  const { isMaximized, toggleMaximize } = useAskSidebar();

  // Fetch chronology events for timeline and date range calculations
  const { data: chronologyData } = useQuery(
    ['chronology-raw', caseId],
    () => chronologyApi.getChronologyByCaseId(caseId, 'REVIEW'),
    { enabled: !!caseId, staleTime: 5 * 60 * 1000, retry: 1 },
  );

  const chronologyEvents: ChronologyEvent[] | undefined = chronologyData?.events;

  // Load reading progress and bookmarks on mount
  useEffect(() => {
    if (caseId) {
      refreshViewSummary(caseId);
      refreshBookmarks(caseId);
    }
  }, [caseId, refreshViewSummary, refreshBookmarks]);

  // View state
  const [activeView, setActiveView] = useState<DocumentsView>('records');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ChunkSearchResult[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const handleSearch = useCallback(async () => {
    const trimmed = searchQuery.trim();
    if (!trimmed || !caseId) return;

    setIsSearching(true);
    setSearchError(null);
    try {
      const chunks = await processingApi.searchChunks(caseId, trimmed, 10);
      setSearchResults(chunks);
    } catch {
      setSearchError('Search failed. Please try again.');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery, caseId]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults(null);
    setSearchError(null);
  }, []);

  // Debounced auto-search
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = searchQuery.trim();
    if (trimmed.length < 3 || !caseId) return;
    debounceRef.current = setTimeout(() => {
      handleSearch();
    }, 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery, caseId, handleSearch]);

  // Determine effective view: search overrides when query is active
  const effectiveView = searchResults !== null ? 'search' : activeView;

  const handleViewChange = useCallback((view: DocumentsView) => {
    if (view !== 'search') {
      clearSearch();
    }
    setActiveView(view);
  }, [clearSearch]);

  // Total pages across all documents
  const totalPages = useMemo(
    () => documents.reduce((sum, d) => sum + (d.pageCount || 0), 0),
    [documents],
  );

  const handleOpenDocument = useCallback((documentId: string, filename: string) => {
    onOpenDocument({ documentId, documentName: filename });
  }, [onOpenDocument]);

  const handleOpenDocumentAtPage = useCallback((documentId: string, filename: string, pageNumber: number) => {
    onOpenDocument({ documentId, documentName: filename, pageNumber });
    // Auto-maximise so the user gets the full doc + outline experience
    if (!isMaximized) toggleMaximize();
  }, [onOpenDocument, isMaximized, toggleMaximize]);

  if (documents.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6 pb-8">
        <FolderOpen className="h-10 w-10 text-medical-200 dark:text-medical-600 mb-3" />
        <h3 className="text-sm font-medium text-medical-600 dark:text-medical-300 mb-1">No records</h3>
        <p className="text-xs text-medical-400 dark:text-medical-500 leading-relaxed">
          Click a citation in the report to view the source record here.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header with record count and progress */}
      <div className="px-3 py-2 border-b border-medical-100 dark:border-medical-700 flex items-center justify-between">
        <p className="text-xs font-medium text-medical-500 dark:text-medical-400">
          {documents.length} record{documents.length !== 1 ? 's' : ''} in case
        </p>
        {totalPages > 0 && (
          <ReadingProgressIndicator
            viewedPages={viewSummary?.totalViewedPages ?? 0}
            totalPages={totalPages}
          />
        )}
      </div>

      {/* Search bar */}
      <div className="px-3 py-2 border-b border-medical-100 dark:border-medical-700">
        <form
          onSubmit={(e) => { e.preventDefault(); handleSearch(); }}
          className="relative flex items-center"
        >
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-medical-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search the records..."
              className="w-full pl-7 pr-7 py-1.5 text-xs border border-medical-200 dark:border-medical-600 rounded-md bg-white dark:bg-medical-900 text-medical-800 dark:text-medical-200 placeholder-medical-400 focus:outline-none focus:ring-1 focus:ring-primary-400 dark:focus:ring-primary-500"
            />
            {(searchQuery || searchResults !== null) && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-medical-400 hover:text-medical-600 dark:hover:text-medical-300"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </form>
      </div>

      {/* View switcher */}
      <div className="border-b border-medical-100 dark:border-medical-700">
        <DocumentsViewSwitcher
          activeView={effectiveView}
          onViewChange={handleViewChange}
          bookmarkCount={bookmarks.length}
        />
      </div>

      {/* Content area */}
      {isSearching ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 text-primary-500 animate-spin" />
          <span className="ml-2 text-xs text-medical-500">Searching records...</span>
        </div>
      ) : searchError ? (
        <div className="flex flex-col items-center py-8 px-4 text-center">
          <AlertCircle className="h-5 w-5 text-amber-500 mb-2" />
          <p className="text-xs text-medical-500">{searchError}</p>
          <button onClick={clearSearch} className="mt-2 text-xs text-primary-600 dark:text-primary-400 hover:underline">
            Clear search
          </button>
        </div>
      ) : effectiveView === 'search' && searchResults ? (
        <div className="flex-1 overflow-y-auto">
          <EnhancedSearchResults
            query={searchQuery}
            results={searchResults}
            documents={documents}
            onOpenDocument={onOpenDocument}
          />
        </div>
      ) : effectiveView === 'bookmarks' ? (
        <div className="flex-1 overflow-y-auto">
          <BookmarksView
            bookmarks={bookmarks}
            documents={documents}
            onOpenDocument={onOpenDocument}
            onRemoveBookmark={removeBookmark}
          />
        </div>
      ) : (
        /* Records view (default) */
        <div className="flex-1 overflow-y-auto">
          <DocumentSummaryCardList
            documents={documents}
            metadata={metadata ?? null}
            chronologyEvents={chronologyEvents}
            viewSummary={viewSummary}
            onOpenDocument={handleOpenDocument}
            onOpenDocumentAtPage={handleOpenDocumentAtPage}
          />
        </div>
      )}
    </div>
  );
};
